import React, {useContext, useEffect, useState} from "react";
import {AiProviderConnectionServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useNavigate} from "react-router-dom";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiProviderConnectionList.module.css";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import AiProviderConnection, {formatAiProviderConnectionType} from "../../domain/AiProviderConnection";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import MarkdownContent from "../common/MarkdownContent";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

function AiProviderConnectionList() {
    const appContext = useContext(AppContextContext);
    const aiProviderConnectionService = useContext(AiProviderConnectionServiceContext);
    const navigate = useNavigate();
    const [tableStateId, setTableStateId] = useState(crypto.randomUUID());

    const title = "AI provider connections";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button type={"primary"}
                            icon={<PlusOutlined/>}
                            onClick={() => navigate("/definitions/ai-provider-connections/add")}>
                        Add
                    </Button>
                </div>

                <AiProviderConnectionsTable key={tableStateId}
                                            columns={getColumns()}/>
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<AiProviderConnection> {
        return [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: (value, connection) => <>
                    <div>
                        <Link to={`/definitions/ai-provider-connections/${connection.id}`}>{value}</Link>
                    </div>

                    {connection.description &&
                        <SingleSummaryBlock>
                            <MarkdownContent children={connection.description}/>
                        </SingleSummaryBlock>
                    }
                </>
            },
            {
                dataIndex: "organization",
                key: "organization.name",
                title: "Owner",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                render: (_value, connection) => connection.owner === ResourceOwnerType.SYSTEM
                    ? <i>System connection</i>
                    : connection.organization?.name
            },
            {
                dataIndex: "type",
                title: "Type",
                width: 220,
                render: value => formatAiProviderConnectionType(value)
            },
            {
                dataIndex: "baseUrl",
                title: "Base URL",
                render: value => <div className={styles.baseUrl}>{value}</div>
            },
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: (_value, connection) => (
                    <Space>
                        <Button
                            icon={<DeleteFilled/>}
                            className={"ant-btn-icon-only"}
                            title={"Delete"}
                            onClick={() => {
                                Modal.confirm({
                                    content: <>Do you really want to delete AI provider connection <b>{connection.name}</b>?</>,
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    okButtonProps: {danger: true},
                                    onOk: () => onDeleteConfirm(connection)
                                });
                            }}
                        />
                    </Space>
                )
            }
        ];
    }

    function onDeleteConfirm(connection: AiProviderConnection) {
        aiProviderConnectionService.delete(connection)
            .then(() => {
                message.success("AI provider connection deleted.");
                setTableStateId(crypto.randomUUID());
            });
    }
}

export default AiProviderConnectionList;

type TableProps = {
    columns: ColumnsType<AiProviderConnection>;
};

function AiProviderConnectionsTable(props: TableProps) {
    const aiProviderConnectionService = useContext(AiProviderConnectionServiceContext);
    const [connections, setConnections] = useState<PagedResult<AiProviderConnection>>();
    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: "AiProviderConnectionList",
    });

    useEffect(() => tableHandler.reload(), []);

    return (
        <>
            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
            </div>

            <Table
                className={styles.table}
                dataSource={connections?.data}
                loading={tableHandler.loading}
                pagination={tableHandler.pagination}
                showSorterTooltip={false}
                rowKey="id"
                onChange={tableHandler.onTableChange}
                columns={props.columns}
            />
        </>
    );

    function reload() {
        return aiProviderConnectionService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);
                setConnections(result);
            });
    }
}
